import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, UserCheck, UserX, Users, Clock, CheckCircle, XCircle, Trash2, Key, Copy } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [resetDialog, setResetDialog] = useState({ open: false, user: null, tempPassword: null });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [isAdmin, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/all-users`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (userId, action) => {
    try {
      const response = await fetch(`${API}/admin/approve-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId, action }),
      });

      if (response.ok) {
        toast.success(`User ${action}d successfully`);
        fetchUsers();
      } else {
        toast.error('Action failed');
      }
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.user) return;
    
    try {
      const response = await fetch(`${API}/admin/delete-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: deleteDialog.user.user_id }),
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        setDeleteDialog({ open: false, user: null });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Delete failed');
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleResetPassword = async (user) => {
    try {
      const response = await fetch(`${API}/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: user.user_id }),
      });

      if (response.ok) {
        const data = await response.json();
        setResetDialog({ open: true, user, tempPassword: data.temporary_password });
        toast.success('Password reset successfully');
      } else {
        toast.error('Reset failed');
      }
    } catch (error) {
      toast.error('Reset failed');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const filteredUsers = users.filter(u => {
    if (filter === 'all') return true;
    return u.status === filter;
  });

  const pendingCount = users.filter(u => u.status === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen theme-bg flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#00f3ff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg">
      <div className="fixed inset-0 theme-gradient opacity-50 pointer-events-none" />

      {/* Header */}
      <header className="relative z-20 px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center theme-text-muted hover:theme-text hover:bg-white/10 transition-all"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="theme-text text-lg md:text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Admin Panel
            </h1>
            <p className="theme-text-muted text-sm">
              Manage users and approvals
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-4 md:px-8 pb-12">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            <div className="card-modern p-4 text-center">
              <Users className="w-5 h-5 theme-text-muted mx-auto mb-2" />
              <p className="text-xl font-bold theme-text">{users.length}</p>
              <p className="theme-text-muted text-xs">Total</p>
            </div>
            <div className="card-modern p-4 text-center">
              <Clock className="w-5 h-5 text-yellow-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-yellow-500">{pendingCount}</p>
              <p className="theme-text-muted text-xs">Pending</p>
            </div>
            <div className="card-modern p-4 text-center">
              <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-green-500">{users.filter(u => u.status === 'approved').length}</p>
              <p className="theme-text-muted text-xs">Approved</p>
            </div>
            <div className="card-modern p-4 text-center">
              <XCircle className="w-5 h-5 text-red-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-red-500">{users.filter(u => u.status === 'rejected').length}</p>
              <p className="theme-text-muted text-xs">Rejected</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {['pending', 'approved', 'rejected', 'all'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === f
                    ? 'bg-white/10 theme-text border border-white/20'
                    : 'theme-text-muted hover:theme-text-secondary border border-transparent'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f === 'pending' && pendingCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-500 text-xs">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* User List */}
          <div className="space-y-3">
            {filteredUsers.length === 0 ? (
              <div className="card-modern p-8 text-center">
                <p className="theme-text-muted">No users found</p>
              </div>
            ) : (
              filteredUsers.map((u) => (
                <div
                  key={u.user_id}
                  className="card-modern p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="theme-text font-medium">{u.name}</p>
                        {u.is_admin && (
                          <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-500 text-xs font-medium">
                            Admin
                          </span>
                        )}
                        {u.status === 'approved' && !u.is_admin && (
                          <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-500 text-xs font-medium">
                            Approved
                          </span>
                        )}
                        {u.status === 'pending' && (
                          <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-600 text-xs font-medium">
                            Pending
                          </span>
                        )}
                        {u.status === 'rejected' && (
                          <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 text-xs font-medium">
                            Rejected
                          </span>
                        )}
                      </div>
                      <p className="theme-text-muted text-sm truncate">{u.email}</p>
                      <p className="theme-text-muted text-xs mt-1 opacity-70">
                        Signed up: {new Date(u.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {u.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleAction(u.user_id, 'approve')}
                            className="p-2 rounded-full bg-green-500/20 text-green-500 hover:bg-green-500/30 transition-all"
                            title="Approve"
                          >
                            <UserCheck size={16} />
                          </button>
                          <button
                            onClick={() => handleAction(u.user_id, 'reject')}
                            className="p-2 rounded-full bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-all"
                            title="Reject"
                          >
                            <UserX size={16} />
                          </button>
                        </>
                      )}
                      {!u.is_admin && (
                        <>
                          <button
                            onClick={() => handleResetPassword(u)}
                            className="p-2 rounded-full bg-blue-500/20 text-blue-500 hover:bg-blue-500/30 transition-all"
                            title="Reset Password"
                          >
                            <Key size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteDialog({ open: true, user: u })}
                            className="p-2 rounded-full bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-all"
                            title="Delete User"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: deleteDialog.user })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="theme-text">Delete User</DialogTitle>
            <DialogDescription className="theme-text-muted">
              Are you sure you want to delete <strong>{deleteDialog.user?.name}</strong>? This will permanently remove their account and all associated data.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => setDeleteDialog({ open: false, user: null })}
              className="btn-secondary flex-1 py-2"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="flex-1 py-2 rounded-full bg-red-500 text-white font-medium hover:bg-red-600 transition-all"
            >
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(open) => !open && setResetDialog({ open: false, user: null, tempPassword: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="theme-text">Password Reset</DialogTitle>
            <DialogDescription className="theme-text-muted">
              Password has been reset for <strong>{resetDialog.user?.name}</strong>. Share this temporary password with them:
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="flex items-center gap-2 p-3 rounded-xl bg-[#00f3ff]/10 border border-[#00f3ff]/30">
              <code className="flex-1 text-[#00f3ff] font-mono font-bold">{resetDialog.tempPassword}</code>
              <button
                onClick={() => copyToClipboard(resetDialog.tempPassword)}
                className="p-2 rounded-lg bg-white/10 theme-text-muted hover:theme-text transition-all"
              >
                <Copy size={16} />
              </button>
            </div>
            <p className="theme-text-muted text-xs mt-2">
              The user should change this password after logging in.
            </p>
          </div>
          <button
            onClick={() => setResetDialog({ open: false, user: null, tempPassword: null })}
            className="btn-primary w-full py-2 mt-4"
          >
            Done
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
